import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';

interface Lead {
  id: string;
  full_name: string;
  email: string;
  message: string;
  investment_range: string;
  status: string;
  created_at: string;
}

const STATUSES = ['new', 'contacted', 'qualified', 'closed'];

export default function AdminLeads() {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchLeads = useCallback(async (status: string) => {
    setLoading(true);
    setError('');
    try {
      const url = status ? `/api/investor-interest?status=${status}` : `/api/investor-interest`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load leads');
      const data = await res.json();
      setLeads(data.leads || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLeads(filter);
  }, [filter, fetchLeads]);

  async function updateStatus(leadId: string, status: string) {
    try {
      const res = await fetch(`/api/investor-interest/${leadId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status })
      });
      if (!res.ok) throw new Error('Update failed');
      setLeads(prev => prev.map(l => l.id === leadId ? { ...l, status } : l));
    } catch {
      setError('Failed to update lead status');
    }
  }

  return (
    <div className="admin-leads-page" data-testid="admin-leads-page">
      <div className="admin-leads-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <img
            src="/brand/nwg-logo-small.png"
            alt="NextWealth Global — Show Us the Problems. We Build the Solutions."
            style={{ width: 40, height: 40, objectFit: 'contain' }}
            data-testid="admin-leads-brand-logo"
          />
          <div>
          <h1>INVESTOR LEAD REVIEW</h1>
          <p>{leads.length} lead{leads.length !== 1 ? 's' : ''} {filter && `• ${filter}`}</p>
          </div>
        </div>
        <div className="admin-leads-actions">
          <select
            value={filter}
            onChange={e => setFilter(e.target.value)}
            data-testid="lead-filter-select"
          >
            <option value="">All statuses</option>
            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <Link to="/" className="admin-leads-back" data-testid="admin-leads-back">
            ← NWG COMMAND CENTER
          </Link>
        </div>
      </div>

      {error && <div className="admin-leads-error" data-testid="admin-leads-error">{error}</div>}

      {loading ? (
        <div className="admin-leads-empty">Loading leads...</div>
      ) : leads.length === 0 ? (
        <div className="admin-leads-empty" data-testid="admin-leads-empty">
          No investor leads yet. Leads submitted on the public landing page will appear here.
        </div>
      ) : (
        <div className="admin-leads-list">
          {leads.map(lead => (
            <div key={lead.id} className="admin-lead-card" data-testid={`lead-card-${lead.id}`}>
              <div className="admin-lead-main">
                <div className="admin-lead-name">{lead.full_name}</div>
                <div className="admin-lead-email">{lead.email}</div>
                {lead.investment_range && (
                  <div className="admin-lead-range">{lead.investment_range}</div>
                )}
                {lead.message && <div className="admin-lead-message">{lead.message}</div>}
                <div className="admin-lead-date">
                  {new Date(lead.created_at).toLocaleString()}
                </div>
              </div>
              <div className="admin-lead-side">
                <span className={`admin-lead-badge badge-${lead.status}`}>{lead.status}</span>
                <select
                  value={lead.status}
                  onChange={e => updateStatus(lead.id, e.target.value)}
                  data-testid={`lead-status-select-${lead.id}`}
                >
                  {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
